import React, {memo} from "react";
import {formikPropertyChanged} from "./index";
import {useTranslation} from "react-i18next";
import FormSection from "../../../../components/FormSection";
import Grid from "@mui/material/Grid";
import IncidentField from "./IncidentField";
import IncidentSelect from "./IncidentSelect";
import FormSubSectionTitle from "./FormSubSectionTitle";
import GridItemOffset from "./GridItemOffset";
import {FieldWithTooltip} from "./FormTooltip";
import Typography from "@mui/material/Typography";


const attributes = [
    'incidentCategoryV1', 'numberOfPeopleInvolved', 'numberOfPeopleCaught',
    'numberOfUninjured', 'uninjuredCompletelyBuried', 'uninjuredPartiallyBuried',
    'uninjuredShallowBuried', 'uninjuredFoundWithBeacon', 'uninjuredFoundWithUcv', 'uninjuredFoundWithProbing',
    'uninjuredFoundWithHearingSight', 'uninjuredSelfRescued',
    'numberOfInjured', 'injuredCompletelyBuried', 'injuredPartiallyBuried', 'injuredShallowBuried',
    'injuredFoundWithBeacon', 'injuredFoundWithUcv', 'injuredFoundWithProbing', 'injuredFoundWithHearingSight',
    'injuredSelfRescued',
    'numberOfDeaths', 'deadMissing', 'deadRecovered', 'deadCompletelyBuried', 'deadPartiallyBuried', 'deadShallowBuried',
    'deadFoundWithBeacon', 'deadFoundWithUcv', 'deadFoundWithProbing', 'deadFoundWithHearingSight', 'deadBySuffocation',
    'deadByTrauma', 'deadByOther',
    'rescueType', 'alpineRescue',
    'burialTimePerson1', 'burialTimePerson2', 'burialTimePerson3',
    'burialDepthPerson1', 'burialDepthPerson2', 'burialDepthPerson3',
    'notes'
]

const Incident = memo(({formik, incidentRegistry, inputDisabled}) => {
    const {t} = useTranslation(['incidents', 'enums']);

    const incidentCategoryRegistry = incidentRegistry.data?.incidentCategoryV1 ?? [];
    const alpineRescueRegistry = incidentRegistry.data?.alpineRescue ?? [];
    const rescueTypeRegistry = incidentRegistry.data?.rescueType ?? [];

    return <FormSection title={t('incidentForm.incidentTitle')}
                        error={attributes.some(attr => Boolean(formik.errors[attr]) && formik.touched[attr])}>
        <Grid container rowSpacing={3} columnSpacing={4}>
            <Grid item xs={12} md={4}>
                <FormSubSectionTitle title={t('incidentForm.incident.incidentCategory')}/>
            </Grid>
            <Grid item xs={12} md={4}>
                <IncidentSelect formik={formik}
                                name="incidentCategoryV1"
                                onChange={formik.handleChange}
                                registry={incidentCategoryRegistry}
                                disabled={inputDisabled}/>
            </Grid>
            <GridItemOffset xs={12} md={4}/>

            <Grid item xs={12} md={4}>
                <FormSubSectionTitle title={t('incidentForm.incident.numberOfPeople')}/>
            </Grid>
            <Grid item xs={12} md={4}>
                <FieldWithTooltip message={t('incidentForm.tooltip.numberOfPeopleInvolved')}>
                    <IncidentField formik={formik}
                                   name="numberOfPeopleInvolved"
                                   type="number"
                                   disabled={inputDisabled}/>
                </FieldWithTooltip>
            </Grid>
            <Grid item xs={12} md={4}>
                <IncidentField formik={formik}
                               name="numberOfPeopleCaught"
                               type="number"
                               disabled={inputDisabled}/>
            </Grid>

            <Grid item xs={12} md={4}>
                <FormSubSectionTitle title={t('incidentForm.incident.uninjured')}/>
            </Grid>
            <Grid item xs={12} md={8}>
                <Grid container rowSpacing={3} columnSpacing={4}>
                    <Grid item xs={12}>
                        <IncidentField formik={formik}
                                       name="numberOfUninjured"
                                       type="number"
                                       disabled={inputDisabled}/>
                    </Grid>
                    {
                        formik.values.numberOfUninjured > 0 && <>
                            <Grid item xs={12}>
                                <Typography variant="body2" color="textSecondary">
                                    {t('incidentForm.incident.ofWhich')}:
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <IncidentField formik={formik}
                                               name='uninjuredCompletelyBuried'
                                               type="number"
                                               InputLabelProps={{shrink: true}}
                                               disabled={inputDisabled}/>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <IncidentField formik={formik}
                                               name='uninjuredPartiallyBuried'
                                               type="number"
                                               InputLabelProps={{shrink: true}}
                                               disabled={inputDisabled}/>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <IncidentField formik={formik}
                                               name='uninjuredShallowBuried'
                                               type="number"
                                               InputLabelProps={{shrink: true}}
                                               disabled={inputDisabled}/>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="body2" color="textSecondary">
                                    {t('incidentForm.incident.ofWhich')}:
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <IncidentField formik={formik}
                                               name='uninjuredFoundWithBeacon'
                                               type="number"
                                               InputLabelProps={{shrink: true}}
                                               disabled={inputDisabled}/>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <IncidentField formik={formik}
                                               name='uninjuredFoundWithUcv'
                                               type="number"
                                               InputLabelProps={{shrink: true}}
                                               disabled={inputDisabled}/>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <IncidentField formik={formik}
                                               name='uninjuredFoundWithProbing'
                                               type="number"
                                               InputLabelProps={{shrink: true}}
                                               disabled={inputDisabled}/>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <IncidentField formik={formik}
                                               name='uninjuredFoundWithHearingSight'
                                               type="number"
                                               InputLabelProps={{shrink: true}}
                                               disabled={inputDisabled}/>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <IncidentField formik={formik}
                                               name='uninjuredSelfRescued'
                                               type="number"
                                               InputLabelProps={{shrink: true}}
                                               disabled={inputDisabled}/>
                            </Grid>
                        </>
                    }
                </Grid>
            </Grid>

            <Grid item xs={12} md={4}>
                <FormSubSectionTitle title={t('incidentForm.incident.injured')}/>
            </Grid>
            <Grid item xs={12} md={8}>
                <Grid container rowSpacing={3} columnSpacing={4}>
                    <Grid item xs={12}>
                        <IncidentField formik={formik}
                                       name="numberOfInjured"
                                       type="number"
                                       disabled={inputDisabled}/>
                    </Grid>
                    {
                        formik.values.numberOfInjured > 0 && <>
                            <Grid item xs={12}>
                                <Typography variant="body2" color="textSecondary">
                                    {t('incidentForm.incident.ofWhich')}:
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <IncidentField formik={formik}
                                               name='injuredCompletelyBuried'
                                               type="number"
                                               InputLabelProps={{shrink: true}}
                                               disabled={inputDisabled}/>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <IncidentField formik={formik}
                                               name='injuredPartiallyBuried'
                                               type="number"
                                               InputLabelProps={{shrink: true}}
                                               disabled={inputDisabled}/>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <IncidentField formik={formik}
                                               name='injuredShallowBuried'
                                               type="number"
                                               InputLabelProps={{shrink: true}}
                                               disabled={inputDisabled}/>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="body2" color="textSecondary">
                                    {t('incidentForm.incident.ofWhich')}:
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <IncidentField formik={formik}
                                               name='injuredFoundWithBeacon'
                                               type="number"
                                               InputLabelProps={{shrink: true}}
                                               disabled={inputDisabled}/>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <IncidentField formik={formik}
                                               name='injuredFoundWithUcv'
                                               type="number"
                                               InputLabelProps={{shrink: true}}
                                               disabled={inputDisabled}/>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <IncidentField formik={formik}
                                               name='injuredFoundWithProbing'
                                               type="number"
                                               InputLabelProps={{shrink: true}}
                                               disabled={inputDisabled}/>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <IncidentField formik={formik}
                                               name='injuredFoundWithHearingSight'
                                               type="number"
                                               InputLabelProps={{shrink: true}}
                                               disabled={inputDisabled}/>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <IncidentField formik={formik}
                                               name='injuredSelfRescued'
                                               type="number"
                                               InputLabelProps={{shrink: true}}
                                               disabled={inputDisabled}/>
                            </Grid>

                        </>
                    }
                </Grid>
            </Grid>

            <Grid item xs={12} md={4}>
                <FormSubSectionTitle title={t('incidentForm.incident.deaths')}/>
            </Grid>
            <Grid item xs={12} md={8}>
                <Grid container rowSpacing={3} columnSpacing={4}>
                    <Grid item xs={12}>
                        <IncidentField formik={formik}
                                       name="numberOfDeaths"
                                       type="number"
                                       disabled={inputDisabled}/>
                    </Grid>
                    {
                        formik.values.numberOfDeaths > 0 && <>
                            <Grid item xs={12}>
                                <Typography variant="body2" color="textSecondary">
                                    {t('incidentForm.incident.ofWhich')}:
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <IncidentField formik={formik}
                                               name='deadMissing'
                                               type="number"
                                               InputLabelProps={{shrink: true}}/>
                            </Grid>
                            <Grid item={12} md={6}>
                                <IncidentField formik={formik}
                                               name='deadRecovered'
                                               type="number"
                                               InputLabelProps={{shrink: true}}/>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="body2" color="textSecondary">
                                    {t('incidentForm.incident.ofWhich')}:
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <IncidentField formik={formik}
                                               name='deadCompletelyBuried'
                                               type="number"
                                               InputLabelProps={{shrink: true}}/>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <IncidentField formik={formik}
                                               name='deadPartiallyBuried'
                                               type="number"
                                               InputLabelProps={{shrink: true}}/>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <IncidentField formik={formik}
                                               name='deadShallowBuried'
                                               type="number"
                                               InputLabelProps={{shrink: true}}/>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="body2" color="textSecondary">
                                    {t('incidentForm.incident.ofWhich')}:
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <IncidentField formik={formik}
                                               name='deadFoundWithBeacon'
                                               type="number"
                                               InputLabelProps={{shrink: true}}/>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <IncidentField formik={formik}
                                               name='deadFoundWithUcv'
                                               type="number"
                                               InputLabelProps={{shrink: true}}/>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <IncidentField formik={formik}
                                               name='deadFoundWithProbing'
                                               type="number"
                                               InputLabelProps={{shrink: true}}/>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <IncidentField formik={formik}
                                               name='deadFoundWithHearingSight'
                                               type="number"
                                               InputLabelProps={{shrink: true}}/>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="body2" color="textSecondary">
                                    {t('incidentForm.incident.ofWhich')}:
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <IncidentField formik={formik}
                                               name='deadBySuffocation'
                                               type="number"
                                               InputLabelProps={{shrink: true}}/>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <IncidentField formik={formik}
                                               name='deadByTrauma'
                                               type="number"
                                               InputLabelProps={{shrink: true}}/>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <IncidentField formik={formik}
                                               name='deadByOther'
                                               type="number"
                                               InputLabelProps={{shrink: true}}/>
                            </Grid>
                        </>
                    }
                </Grid>
            </Grid>

            <Grid item xs={12} md={4}>
                <FormSubSectionTitle title={t('incidentForm.incident.rescue')}/>
            </Grid>
            <Grid item xs={12} md={4} offset={{ xs: 0, md: 8}}>
                <IncidentSelect formik={formik}
                                name="alpineRescue"
                                registry={alpineRescueRegistry}
                                disabled={inputDisabled}/>
            </Grid>
            <Grid item xs={12} md={4} offset={{ xs: 0, md: 8}}>
                <IncidentSelect formik={formik}
                                name="rescueType"
                                registry={rescueTypeRegistry}
                                disabled={inputDisabled}/>
            </Grid>

            <Grid item xs={12} md={4}>
                <FormSubSectionTitle title={t('incidentForm.incident.burialTimeAndDepth')}/>
            </Grid>
            <Grid item xs={12} md={8}>
                <Grid container rowSpacing={3} columnSpacing={4}>
                    <Grid item xs={12} md={6} offset={{ xs: 0, md: 8}}>
                        <IncidentField formik={formik} name='burialTimePerson1' type="number" />
                    </Grid>
                    <Grid item xs={12} md={6} offset={{ xs: 0, md: 8}}>
                        <IncidentField formik={formik} name='burialDepthPerson1' type="number" />
                    </Grid>
                    <Grid item xs={12} md={6} offset={{ xs: 0, md: 8}}>
                        <IncidentField formik={formik} name='burialTimePerson2' type="number" />
                    </Grid>
                    <Grid item xs={12} md={6} offset={{ xs: 0, md: 8}}>
                        <IncidentField formik={formik} name='burialDepthPerson2' type="number" />
                    </Grid>
                    <Grid item xs={12} md={6} offset={{ xs: 0, md: 8}}>
                        <IncidentField formik={formik} name='burialTimePerson3' type="number" />
                    </Grid>
                    <Grid item xs={12} md={6} offset={{ xs: 0, md: 8}}>
                        <IncidentField formik={formik} name='burialDepthPerson3' type="number" />
                    </Grid>
                </Grid>
            </Grid>

            <Grid item xs={12} md={4}>
                <FormSubSectionTitle title={t('incidentForm.incident.notes')}/>
            </Grid>
            <Grid item xs={12} md={8} offset={{xs: 0, md: 8}}>
                <IncidentField formik={formik}
                               name="notes"
                               rows={8}
                               multiline
                               InputProps={{
                                   readOnly: inputDisabled,  // we use readonly and not disable because disable make the input no more scrollable,
                               }}/>
            </Grid>
        </Grid>
    </FormSection>

}, (prevProps, nextProps) => (formikPropertyChanged(prevProps, nextProps, attributes)));


export default Incident;